import { useEffect, useMemo, useState } from "react";
import { DatePicker, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { CalendarOutlined, BookOutlined } from "@ant-design/icons";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as the other Accounts report screens) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── Receipt/Payment chips, same visual family as BankStatement's Credit/Debit ─── */
const amountChipStyle = (kind) => {
  const palette = {
    receipt: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    payment: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  }[kind];

  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12.5,
    fontWeight: 700,
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "LEDGERNAME", label: "Ledger" },
  { key: "GROUPNAME", label: "Group" },
];
const secondaryFields = [
  { key: "CREDIT_RECEIPT", label: "Receipt" },
  { key: "DEBIT_PAYMENT", label: "Payment" },
  { key: "PAYMODE", label: "Mode" },
];

const JournalRegister = () => {
  const [rawData, setRawData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ---- data / API logic (unchanged endpoint + query params/format) ---- */
  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const fromDate = dayjs(dates[0]).format("MM/DD/YYYY");
    const toDate = dayjs(dates[1]).format("MM/DD/YYYY");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=DAY_TRANSACTIONS&where=SDATE>='${fromDate}' and SDATE<='${toDate}'&order=VNO`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching journal register details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    { title: "SNO", dataIndex: "SNO", key: "SNO" },
    { title: "Voucher No", dataIndex: "VNO", key: "VNO" },
    {
      title: "Date",
      dataIndex: "SDATE",
      key: "SDATE",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Group Name",
      dataIndex: "GROUPNAME",
      key: "GROUPNAME",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    { title: "Ledger Name", dataIndex: "LEDGERNAME", key: "LEDGERNAME" },
    { title: "Mode of Pay", dataIndex: "PAYMODE", key: "PAYMODE" },
    {
      title: "Receipt",
      dataIndex: "CREDIT_RECEIPT",
      key: "CREDIT_RECEIPT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("receipt")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Payment",
      dataIndex: "DEBIT_PAYMENT",
      key: "DEBIT_PAYMENT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("payment")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    { title: "Account Name", dataIndex: "ACCNAME", key: "ACCNAME" },
    { title: "Ref.No", dataIndex: "REFNO", key: "REFNO" },
    { title: "Narration", dataIndex: "NARR2", key: "NARR2" },
  ];

  const totals = useMemo(() => {
    return rawData.reduce(
      (acc, item) => {
        acc.receipt += Number(item.CREDIT_RECEIPT || 0);
        acc.payment += Number(item.DEBIT_PAYMENT || 0);
        return acc;
      },
      { receipt: 0, payment: 0 },
    );
  }, [rawData]);

  const formattedData = useMemo(
    () =>
      rawData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        CREDIT_RECEIPT: Number(item.CREDIT_RECEIPT || 0),
        DEBIT_PAYMENT: Number(item.DEBIT_PAYMENT || 0),
      })),
    [rawData],
  );

  const exportData = [
    ...formattedData.map((item) => ({
      ...item,
      SDATE: item?.SDATE ? dayjs(item.SDATE).format("DD/MMM/YYYY") : "",
      CREDIT_RECEIPT: item.CREDIT_RECEIPT.toFixed(2),
      DEBIT_PAYMENT: item.DEBIT_PAYMENT.toFixed(2),
    })),
    {
      serialNo: "Total",
      SNO: "",
      VNO: "",
      SDATE: "",
      GROUPNAME: "",
      LEDGERNAME: "",
      PAYMODE: "",
      CREDIT_RECEIPT: totals.receipt.toFixed(2),
      DEBIT_PAYMENT: totals.payment.toFixed(2),
      ACCNAME: "",
      REFNO: "",
      NARR2: "",
    },
  ];

  return (
    <div className={styles.page}>
      <Header setOpen={setOpen} />
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Journal Register"
        subtitle="Accounts · Journal Register"
        icon={<BookOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={rawData.length}
        visible={rawData.length > 0}
        stats={[
          { label: "Records", value: rawData.length, color: "blue" },
          {
            label: "Receipt",
            value: totals.receipt.toFixed(2),
            color: "green",
          },
          {
            label: "Payment",
            value: totals.payment.toFixed(2),
            color: "red",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="JournalRegister"
            totals={{
              totalReceipt: totals.receipt.toFixed(2),
              totalPayment: totals.payment.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="journal-reg-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No journal entries found for the selected date range"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.receipt.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.payment.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
          </Table.Summary.Row>
        )}
      />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default JournalRegister;
